import { ArrowsClockwise } from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';
import { useWorkspace } from '@/app/providers/workspace-provider';
import { createTaskRepository } from '@/data/repositories/task-repository';
import type { RecurrenceFrequency, Task } from '@/data/db';
import { Button } from '@/components/primitives/button';
import { Dialog } from '@/components/primitives/dialog';
import { Field, Select, TextInput } from '@/components/primitives/field';
import { useToast } from '@/components/feedback/toast';
import { cn } from '@/lib/cn';
import { RECURRENCE_FREQUENCY_LABELS, describeRecurrence } from './recurrence';

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'] as const;

/**
 * 重複規則（spec 9.4）。
 * 產生策略是「完成後才產生下一次」，所以畫面要說清楚：
 * 沒有完成就不會自動產生，也不會另行提醒。
 */
export function RecurrenceEditor({ task }: { task: Task }) {
  const { db, save } = useWorkspace();
  const toast = useToast();
  const repository = useMemo(() => createTaskRepository(db), [db]);

  const rule = useLiveQuery(
    async () => (task.recurrenceRuleId ? db.recurrenceRules.get(task.recurrenceRuleId) : undefined),
    [db, task.recurrenceRuleId],
  );

  const [editing, setEditing] = useState(false);
  const [frequency, setFrequency] = useState<RecurrenceFrequency>('weekly');
  const [weekdays, setWeekdays] = useState<number[]>([1]);
  const [monthDay, setMonthDay] = useState(1);
  const [yearMonth, setYearMonth] = useState(1);
  const [yearDay, setYearDay] = useState(1);
  const [endDate, setEndDate] = useState('');

  /** 開啟對話框前先帶入現有規則，否則「修改規則」等於默默把舊規則換掉。 */
  function openEditor() {
    setFrequency(rule?.frequency ?? 'weekly');
    setWeekdays(rule?.weekdays ?? [1]);
    setMonthDay(rule?.monthDay ?? 1);
    setYearMonth(rule?.yearMonth ?? 1);
    setYearDay(rule?.yearDay ?? 1);
    setEndDate(rule?.endDate ?? '');
    setEditing(true);
  }

  async function submit() {
    const input = {
      frequency,
      weekdays: frequency === 'weekly' ? weekdays : null,
      monthDay: frequency === 'monthly' ? monthDay : null,
      yearMonth: frequency === 'yearly' ? yearMonth : null,
      yearDay: frequency === 'yearly' ? yearDay : null,
      endDate: endDate || null,
    };
    const existing = rule;

    await save.run(async () => {
      // 已經有規則就地更新，不要建新的再讓舊的變孤兒被刪掉。
      if (existing) await repository.updateRecurrence(existing.id, input);
      else await repository.setRecurrence(task.id, input);
    });
    toast.show({ message: existing ? '已更新重複規則' : '已設定重複規則' });
    setEditing(false);
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[0.8125rem] font-medium text-text-muted">重複</h3>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ArrowsClockwise size={14} />}
          onClick={openEditor}
        >
          {rule ? '修改規則' : '設定重複'}
        </Button>
      </div>

      {rule ? (
        <div className="space-y-2 rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-3">
          <p className="text-[0.875rem]">{describeRecurrence(rule)}</p>
          <p className="text-[0.75rem] leading-relaxed text-text-muted">
            完成這一次之後才會產生下一次。沒有完成就不會自動產生，也不會另外提醒。
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              void save.run(async () => {
                await repository.detachRecurrence(task.id);
                toast.show({ message: '已解除重複', detail: '這一筆不再依規則產生下一次。' });
              })
            }
          >
            只修改本次（解除重複）
          </Button>
        </div>
      ) : (
        <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-2.5 text-[0.8125rem] text-text-muted">
          這件工作不會重複。設定之後，每次完成都會自動產生下一次。
        </p>
      )}

      <Dialog
        open={editing}
        onOpenChange={setEditing}
        title={rule ? '修改重複規則' : '設定重複規則'}
        description="下一次只在這一次被標示完成時才產生，不會一次寫入一整年的資料。"
        size="md"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              取消
            </Button>
            <Button variant="primary" size="sm" onClick={() => void submit()}>
              儲存規則
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {!task.dueDate && (
            <p className="rounded-[var(--radius-control-lg)] bg-warning/10 px-4 py-2.5 text-[0.8125rem] text-warning">
              這件工作還沒有到期日。沒有到期日就算不出下一次的日期，記得先補上。
            </p>
          )}

          <Field label="頻率">
            {({ id }) => (
              <Select
                id={id}
                value={frequency}
                onValueChange={(value) => setFrequency(value as RecurrenceFrequency)}
                options={Object.entries(RECURRENCE_FREQUENCY_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </Field>

          {frequency === 'weekly' && (
            <fieldset className="space-y-2">
              <legend className="text-[0.8125rem] font-medium text-text-muted">星期</legend>
              <div className="flex gap-1.5">
                {WEEKDAY_LABELS.map((label, day) => (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={weekdays.includes(day)}
                    onClick={() =>
                      setWeekdays((current) =>
                        current.includes(day)
                          ? current.filter((value) => value !== day)
                          : [...current, day].sort(),
                      )
                    }
                    className={cn(
                      'size-9 rounded-full border text-[0.8125rem] transition-colors duration-200',
                      weekdays.includes(day)
                        ? 'border-accent bg-accent-soft text-text'
                        : 'border-border text-text-muted hover:border-border-strong',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {frequency === 'monthly' && (
            <Field label="每月幾號" hint="31 號遇到 2 月會自動取當月最後一天，不會順延到 3 月。">
              {({ id }) => (
                <TextInput
                  id={id}
                  type="number"
                  min={1}
                  max={31}
                  value={monthDay}
                  onChange={(event) => setMonthDay(Number(event.target.value))}
                />
              )}
            </Field>
          )}

          {frequency === 'yearly' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="月">
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="number"
                    min={1}
                    max={12}
                    value={yearMonth}
                    onChange={(event) => setYearMonth(Number(event.target.value))}
                  />
                )}
              </Field>
              <Field label="日">
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="number"
                    min={1}
                    max={31}
                    value={yearDay}
                    onChange={(event) => setYearDay(Number(event.target.value))}
                  />
                )}
              </Field>
            </div>
          )}

          <Field label="重複到" hint="留空代表持續重複。">
            {({ id }) => (
              <TextInput
                id={id}
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>
    </section>
  );
}
